import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef } from 'react';

// The radar tiles are proxied through pitpay-graphql (so the Xweather secret
// never reaches the browser). Derive that REST base from the GraphQL endpoint.
const REST_BASE = (process.env.REACT_APP_GRAPHQL || '').replace(
  /\/graphql\/?$/i,
  ''
);

const tileTemplate = (layer, time) =>
  `${REST_BASE}/weather/radar/${layer}/{z}/{x}/{y}/${time}`;

// Xweather time-token for a frame's minute offset: negative = past, 0 = now,
// positive = forecast. Used by the time-aware weather overlays below.
function frameToken(mins) {
  if (mins > 0) return `+${mins}min`;
  if (mins < 0) return `${mins}min`;
  return 'current';
}

const TILE_OPTS = { maxZoom: 13, minZoom: 4, maxNativeZoom: 13, tileSize: 256 };

// Map style — tweak these to restyle the map (any layer the proxy whitelists:
// bases flat / flat-dk / satellite / blue-marble; overlays admin / states /
// counties / roads / water / rivers). No backend change needed.
const BASE_LAYER = 'flat-dk';
const TOP_OVERLAYS = ['admin']; // drawn above the radar for labels + borders

// Each toggle pill (keyed by its label) maps to an Xweather raster overlay.
// `future` (when present) is the forecast variant swapped in for +min frames;
// `timeAware` layers follow the timeline scrubber, the rest always show `current`.
// `z` fixes the stacking order so overlays don't reorder as they toggle.
const OVERLAY_LAYERS = {
  Precipitation: {
    base: 'radar',
    future: 'fradar',
    timeAware: true,
    opacity: 0.8,
    z: 2,
  },
  Wind: {
    base: 'wind-speeds',
    future: 'fwind-speeds',
    timeAware: true,
    opacity: 0.5,
    z: 3,
  },
  Alerts: { base: 'alerts', opacity: 0.45, z: 4 },
  Lightning: { base: 'lightning-strikes', opacity: 0.9, z: 5 },
};

// Storm cells aren't a raster tile layer — they're point data we draw as
// severity-coded markers with a plain-language label + a motion arrow. Keyed by
// the `severity` string from the view model.
const STORM_SEVERITY = {
  rotating: { color: '#ff3b30', label: 'Tornado risk' },
  hail: { color: '#ffb020', label: 'Hail storm' },
  general: { color: '#ffd60a', label: 'Storm' },
};

// Small white lightning bolt for the marker core (dark fill for contrast on the
// bright severity dot).
const BOLT_SVG =
  '<svg viewBox="0 0 24 24" aria-hidden="true"><path fill="#1a1a1a" d="M13 2 4 14h6l-1 8 9-12h-6z"/></svg>';

// 16-point compass → bearing degrees, for turning "NE 25 mph" into an arrow.
const CARDINAL = {
  N: 0,
  NNE: 22.5,
  NE: 45,
  ENE: 67.5,
  E: 90,
  ESE: 112.5,
  SE: 135,
  SSE: 157.5,
  S: 180,
  SSW: 202.5,
  SW: 225,
  WSW: 247.5,
  W: 270,
  WNW: 292.5,
  NW: 315,
  NNW: 337.5,
};

// Parse the view model's motion string ("NE 25 mph") into { bearing, speed }.
function parseMotion(s) {
  if (!s) return null;
  const m = String(s).match(/^([NSEW]{1,3})\s+(\d+(?:\.\d+)?)/i);
  if (!m) return null;
  const bearing = CARDINAL[m[1].toUpperCase()];
  const speed = Number(m[2]);
  if (bearing == null || !speed) return null;
  return { bearing, speed };
}

// Destination lat/lng a given distance (km) along a bearing from a start point.
function projectLatLng(lat, lng, bearingDeg, distKm) {
  const R = 6371;
  const d = distKm / R;
  const brng = (bearingDeg * Math.PI) / 180;
  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;
  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(d) + Math.cos(lat1) * Math.sin(d) * Math.cos(brng)
  );
  const lng2 =
    lng1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(d) * Math.cos(lat1),
      Math.cos(d) - Math.sin(lat1) * Math.sin(lat2)
    );
  return [(lat2 * 180) / Math.PI, (lng2 * 180) / Math.PI];
}

/**
 * Real Leaflet radar map: dark Xweather base + animated radar overlay, centered
 * on the venue. `mins` is the active frame offset; changing it swaps the radar
 * layer to that timestamp without tearing down the map.
 */
export default function RadarMap({
  lat,
  lng,
  name,
  mins,
  layers,
  stormCells,
  interactive = true,
  onMap,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  // Active Leaflet overlays keyed by pill label: { [label]: { layer, key } }.
  const overlaysRef = useRef({});
  // Storm-cell marker group (drawn separately from the raster overlays).
  const stormLayerRef = useRef(null);

  // Init once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    if (lat == null || lng == null) return undefined;

    const map = L.map(containerRef.current, {
      center: [lat, lng],
      zoom: 8,
      zoomControl: false,
      attributionControl: false,
      dragging: interactive,
      scrollWheelZoom: false,
      doubleClickZoom: interactive,
      touchZoom: interactive,
      keyboard: false,
    });

    L.tileLayer(tileTemplate(BASE_LAYER, 'current'), {
      ...TILE_OPTS,
      zIndex: 1,
    }).addTo(map);

    // Weather overlays (radar/wind/lightning/alerts) are added by the reconciler
    // effect below, driven by the `layers` toggles — nothing to add here.

    // Orientation overlays (labels/borders) sit above the weather layers so they
    // stay readable through them.
    TOP_OVERLAYS.forEach((ov, i) => {
      L.tileLayer(tileTemplate(ov, 'current'), {
        ...TILE_OPTS,
        zIndex: 20 + i,
      }).addTo(map);
    });

    // Geo-anchored venue marker + label — both pinned to the real location, so
    // they pan/zoom together with the map (the old screen-center pin is gone).
    const venueMarker = L.circleMarker([lat, lng], {
      radius: 6,
      color: '#ffffff',
      weight: 2,
      fillColor: '#fa4616',
      fillOpacity: 1,
      interactive: false,
    }).addTo(map);
    if (name) {
      venueMarker
        .bindTooltip(name, {
          permanent: true,
          direction: 'bottom',
          offset: [0, 6],
          className: 'radar-venue-tip',
        })
        .openTooltip();
    }

    mapRef.current = map;
    if (onMap) onMap(map);

    return () => {
      map.remove();
      mapRef.current = null;
      overlaysRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Recenter if the venue location changes.
  useEffect(() => {
    if (mapRef.current && lat != null && lng != null) {
      mapRef.current.setView([lat, lng], mapRef.current.getZoom(), {
        animate: false,
      });
    }
  }, [lat, lng]);

  // Reconcile the weather overlays whenever a toggle flips or the timeline frame
  // moves. Each pill maps to at most one live tile layer; we add/replace/remove
  // to match the desired state. When a layer's tiles change (toggle on, or a
  // time-aware layer's frame moved) the new layer is added first and the old one
  // dropped on load, so overlays don't flash empty mid-swap.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const timers = [];
    const drop = (layer) => {
      if (layer && map.hasLayer(layer)) map.removeLayer(layer);
    };
    Object.keys(OVERLAY_LAYERS).forEach((pill) => {
      const cfg = OVERLAY_LAYERS[pill];
      const want = !!(layers && layers[pill]);
      const active = overlaysRef.current[pill];

      // Toggle off (or gone): tear down the live layer AND any still-in-flight
      // predecessor from a mid-swap, so nothing gets orphaned on the map.
      if (!want) {
        if (active) {
          drop(active.layer);
          drop(active.prev);
          delete overlaysRef.current[pill];
        }
        return;
      }

      const code =
        cfg.timeAware && mins > 0 && cfg.future ? cfg.future : cfg.base;
      const time = cfg.timeAware ? frameToken(mins) : 'current';
      const key = `${code}/${time}`;
      if (active && active.key === key) return; // already showing the right tiles

      // Clear a lingering predecessor from a prior swap before starting another,
      // so fast autoplay can't stack more than the current + one fading frame.
      if (active) drop(active.prev);

      const next = L.tileLayer(tileTemplate(code, time), {
        ...TILE_OPTS,
        opacity: cfg.opacity,
        zIndex: cfg.z,
      });
      next.addTo(map);
      const prev = active && active.layer;
      overlaysRef.current[pill] = { layer: next, key, prev };

      if (prev) {
        // Drop the outgoing frame once the new one paints (fallback on a timer),
        // and forget it so teardown doesn't try to remove it twice.
        const dropPrev = () => {
          drop(prev);
          const entry = overlaysRef.current[pill];
          if (entry && entry.prev === prev) entry.prev = null;
        };
        next.once('load', dropPrev);
        timers.push(setTimeout(dropPrev, 1500)); // fallback if 'load' never fires
      }
    });

    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, mins]);

  // Storm-cell markers: per cell at its real lat/lng, a severity-colored dot
  // with a plain-language label (what it is + when it arrives) and an arrow
  // showing which way it's moving. Rebuilt when the toggle flips or cells
  // refresh. Cells without coords are skipped (they still show in the side list).
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    if (stormLayerRef.current) {
      map.removeLayer(stormLayerRef.current);
      stormLayerRef.current = null;
    }

    if (!(layers && layers['Storm Cells'])) return undefined;
    const cells = (stormCells || []).filter(
      (c) => c.lat != null && c.lng != null
    );
    if (!cells.length) return undefined;

    const group = L.layerGroup();
    cells.forEach((c) => {
      const sev = STORM_SEVERITY[c.severity] || STORM_SEVERITY.general;

      // Motion arrow — a dashed line pointing where the storm is heading, with a
      // triangle at the tip. Length scales with speed but is clamped so a fast
      // cell doesn't draw a line across the whole map.
      const mv = parseMotion(c.motion);
      if (mv) {
        const km = Math.min(45, Math.max(10, mv.speed * 1.2));
        const end = projectLatLng(c.lat, c.lng, mv.bearing, km);
        L.polyline([[c.lat, c.lng], end], {
          color: sev.color,
          weight: 3,
          opacity: 0.75,
          dashArray: '6 6',
        }).addTo(group);
        L.marker(end, {
          interactive: false,
          icon: L.divIcon({
            className: 'wc-storm-arrow',
            html: `<div class="wc-storm-arrow-head" style="transform:rotate(${mv.bearing}deg);border-bottom-color:${sev.color}"></div>`,
            iconSize: [16, 16],
            iconAnchor: [8, 8],
          }),
        }).addTo(group);
      }

      // Plain-language label: "Tornado risk · ~22m away" (falls back to distance
      // when ETA is unknown, or just the type when neither is available).
      const bits = [sev.label];
      if (c.eta && c.eta !== '—') bits.push(`${c.eta} away`);
      else if (c.distanceMi != null) bits.push(`${c.distanceMi} mi`);

      const marker = L.marker([c.lat, c.lng], {
        icon: L.divIcon({
          className: 'wc-storm-icon',
          html: `<div class="wc-storm-dot sev-${c.severity}"><span class="wc-storm-glyph">${BOLT_SVG}</span></div>`,
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        }),
      });
      marker.bindTooltip(bits.join(' · '), {
        permanent: true,
        direction: 'bottom',
        offset: [0, 10],
        className: 'radar-storm-tip',
      });
      marker.addTo(group);
    });
    group.addTo(map);
    stormLayerRef.current = group;
    return undefined;
  }, [layers, stormCells]);

  return <div ref={containerRef} className="radar-leaflet" />;
}
